import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeExpressCheckoutElementConfirmEvent, StripeExpressCheckoutElementReadyEvent, StripePaymentElement } from '@stripe/stripe-js';
import { api } from '../lib/api.js';
import { WEB_URL } from '../lib/platform.js';
import { platformStripe, siteStripe, stripeAppearance } from '../lib/stripe.js';
import { APPLE_PAY_MERCHANT_ID, NATIVE_SHEET, nativeWallets, presentNativeWallet } from '../lib/nativePay.js';
import { Svg } from '../art/icons.js';
import { Spinner, Switch } from './ui.js';

/*
 * Paying (doc 02 §2), with one tap wherever possible:
 *  - saved card: the big button charges it (the server clones the platform card to the site account)
 *  - new card: the card form sits inline (Payment Element on a platform SetupIntent); the big button sets it up, then charges
 *  - Apple Pay / Google Pay: a row in "Pay with" when the device has one; choosing it swaps the big button for the native
 *    Apple Pay / Google Pay button (Apple requires their button to open the sheet), which creates the order and pays in one tap
 *  - in the iPhone and Android apps the same wallet row is backed by Stripe's native sheet (lib/nativePay.ts, docs/32): the
 *    web's Express Checkout Element cannot show Apple Pay inside a WKWebView
 * Link is off everywhere (Saif's decision). 3-D Secure, declines and retries are handled here; the server owns every amount.
 */

export interface PayablePending {
  kind: 'order' | 'topup'; id: string; cardPence: number; requiresPayment: boolean;
  /** A guest order (docs/30): its routes live under /api/guest and carry the link token on every call and on the 3-D Secure return. */
  base?: string | undefined; query?: string | undefined; returnExtra?: string | undefined;
}
export type PayRes = { status: 'succeeded' | 'requires_action' | 'requires_payment' | 'failed' | 'cancelled' | 'wallet'; clientSecret?: string; paymentIntentId?: string; stripeAccountId?: string; publishableKey?: string | null; applePayMerchantId?: string; message?: string; payLabel?: string };
export const payBase = (p: PayablePending) => p.base ?? (p.kind === 'order' ? `/api/orders/${p.id}` : `/api/credit/topups/${p.id}`);
export const payQuery = (p: PayablePending) => p.query ?? '';
export class PayError extends Error {
  override name = 'PayError';
}

/** After the server charged a card: done, needs 3-D Secure (handled on the site's Stripe instance), or failed with a message. */
export async function settleCard(pk: string, accountId: string, p: PayablePending, r: PayRes): Promise<string> {
  if (r.status === 'succeeded') return r.payLabel ?? 'card';
  if (r.status === 'cancelled') throw new PayError(r.message ?? 'This order was cancelled.');
  if (r.status === 'requires_action' && r.clientSecret) {
    const st = await siteStripe(pk, accountId);
    if (!st) throw new PayError('Stripe did not load');
    const { error } = await st.handleNextAction({ clientSecret: r.clientSecret });
    if (error) throw new PayError(error.message ?? 'Authentication failed');
    const c = await api<PayRes>(`${payBase(p)}/confirm${payQuery(p)}`, { body: {} });
    if (c.status === 'succeeded') return c.payLabel ?? 'card';
    throw new PayError(c.message ?? 'The payment did not go through.');
  }
  throw new PayError(r.message ?? 'The payment did not go through.');
}

export async function paySavedCard(pk: string, accountId: string, p: PayablePending, paymentMethodId: string): Promise<string> {
  return settleCard(pk, accountId, p, await api<PayRes>(`${payBase(p)}/pay`, { body: { method: 'saved_card', paymentMethodId } }));
}

export async function payNewCard(pk: string, accountId: string, p: PayablePending, paymentMethodId: string, remember: boolean): Promise<string> {
  return settleCard(pk, accountId, p, await api<PayRes>(`${payBase(p)}/pay`, { body: { method: 'new_card', paymentMethodId, remember } }));
}

/* ---------- new card, inline ---------- */

export interface NewCardHandle {
  /** Sets the card up on the platform (3-D Secure if needed) and returns its payment method id. */
  submit(): Promise<string>;
}

interface NewCardProps {
  pk: string;
  remember: boolean;
  onRemember: (v: boolean) => void;
  onReady?: () => void;
  /** The customer just chose "Another card": once the form is ready, scroll it into view (clear of the pay bar) and focus the card number. */
  reveal?: boolean;
}

/** The card form. Mount it when "New card" is chosen; call `submit()` from the pay button. */
export const NewCardForm = forwardRef<NewCardHandle, NewCardProps>(function NewCardForm({ pk, remember, onRemember, onReady, reveal }, ref) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [stripe] = useState<Promise<Stripe | null>>(() => platformStripe(pk));
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    api<{ clientSecret: string }>('/api/me/payment-methods/setup', { body: {} }).then((r) => setClientSecret(r.clientSecret)).catch((e: Error) => setErr(e.message));
  }, []);
  const ready = (el: StripePaymentElement) => {
    onReady?.();
    if (!reveal) return;
    box.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // Focus after the scroll has settled, so the keyboard opening does not fight the scroll.
    setTimeout(() => {
      try {
        el.focus();
      } catch {
        /* older Stripe.js without focus on this element */
      }
    }, 350);
  };
  return (
    <div ref={box} className="newcard">
      {err ? <p className="err">{err}</p> : !clientSecret ? <div className="center" style={{ padding: 16 }}><Spinner /></div> : (
        <Elements stripe={stripe} options={{ clientSecret, appearance: stripeAppearance() }}>
          <NewCardInner ref={ref} remember={remember} onRemember={onRemember} onReady={ready} />
        </Elements>
      )}
    </div>
  );
});

const NewCardInner = forwardRef<NewCardHandle, { remember: boolean; onRemember: (v: boolean) => void; onReady: (el: StripePaymentElement) => void }>(function NewCardInner({ remember, onRemember, onReady }, ref) {
  const stripe = useStripe();
  const elements = useElements();
  useImperativeHandle(ref, () => ({
    async submit() {
      if (!stripe || !elements) throw new PayError('The card form is still loading');
      const { error, setupIntent } = await stripe.confirmSetup({ elements, redirect: 'if_required', confirmParams: { return_url: `${WEB_URL}/pay/return` } });
      if (error) throw new PayError(error.message ?? 'Card not accepted');
      const pm = typeof setupIntent?.payment_method === 'string' ? setupIntent.payment_method : setupIntent?.payment_method?.id;
      if (!pm) throw new PayError('No card was saved');
      return pm;
    },
  }), [stripe, elements]);
  return (
    <div className="col" style={{ gap: 10 }}>
      <div className="stripe-box"><PaymentElement onReady={onReady} options={{ layout: 'tabs', terms: { card: 'never' }, wallets: { applePay: 'never', googlePay: 'never' } }} /></div>
      <div className="row" style={{ padding: '0 4px' }}><span className="grow" style={{ fontSize: 14 }}>Remember this card</span><Switch on={remember} onChange={onRemember} label="Remember this card" /></div>
    </div>
  );
});

/* ---------- Apple Pay / Google Pay ---------- */

export type Wallets = { applePay: boolean; googlePay: boolean };
export const walletUsable = (w: Wallets | null | undefined): boolean => !!w && (w.applePay || w.googlePay);

/** What this browser says about wallet support, logged next to Stripe's answer so a support question can be answered from the console. */
export function walletEnv() {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Android/.test(ua);
  const standalone = (navigator as { standalone?: boolean }).standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  const APS = (window as { ApplePaySession?: { canMakePayments?: () => boolean } }).ApplePaySession;
  let canMakePayments: boolean | null = null;
  try {
    canMakePayments = APS?.canMakePayments ? APS.canMakePayments() : null;
  } catch {
    canMakePayments = null;
  }
  return { standalone, ios, safari, hasApplePaySession: !!APS, canMakePayments, android: /Android/.test(ua), chrome: /Chrome|CriOS/.test(ua) };
}

const APPLE_LOGO = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>';
const GOOGLE_G = '<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>';

/** The Apple Pay or Google Pay mark, sized like the VISA / CARD chips on the pay rows. */
export function WalletMark({ wallet }: { wallet: 'apple' | 'google' }) {
  return wallet === 'apple'
    ? <span className="paymark apple" role="img" aria-label="Apple Pay"><Svg html={APPLE_LOGO} />Pay</span>
    : <span className="paymark google" role="img" aria-label="Google Pay"><Svg html={GOOGLE_G} />Pay</span>;
}

export interface WalletProps {
  pk: string;
  accountId: string;
  /** What the wallet will charge, in pence. Elements re-sends it to Stripe whenever it changes, so the sheet shows the current total. */
  amountPence: number;
  /** True while the wallet row is chosen: the native button then sits where the yellow button was. Otherwise it stays mounted off-screen. */
  selected: boolean;
  /** Creates (or returns the existing) order or top-up to pay for. */
  createPayable: () => Promise<PayablePending>;
  onPaid: (payLabel: string) => void;
  onError: (message: string) => void;
  /** Stripe's answer, once: which wallets this device can use. Both false when none, after a load error, or if Stripe never answers. */
  onAvailable: (wallets: Wallets) => void;
  onBusy?: (busy: boolean) => void;
}

/**
 * Mount once per bag or sheet (inside the pay bar) and keep it mounted: the Express Checkout Element is created a single
 * time, answers `ready` with the wallets it can use, and is moved on-screen only while the wallet row is chosen.
 */
export function WalletButton(props: WalletProps) {
  if (NATIVE_SHEET) return <NativeWalletButton {...props} />;
  return <WebWalletButton {...props} />;
}

function WebWalletButton(props: WalletProps) {
  const [stripe] = useState<Promise<Stripe | null>>(() => siteStripe(props.pk, props.accountId));
  if (props.amountPence <= 0) return null;
  // Card only (Apple Pay and Google Pay are card): the server's intent is card only, and deferred mode would otherwise offer every Dashboard method.
  return (
    <Elements stripe={stripe} options={{ mode: 'payment', amount: props.amountPence, currency: 'gbp', allowedPaymentMethodTypes: ['card'], appearance: stripeAppearance() }}>
      <WalletInner {...props} />
    </Elements>
  );
}

function WalletInner(props: WalletProps) {
  const stripe = useStripe();
  const elements = useElements();
  const latest = useRef(props);
  latest.current = props;
  const [state, setState] = useState<'loading' | 'ready' | 'none'>('loading');
  const [paying, setPaying] = useState(false);
  const answered = useRef(false);
  const settle = (w: Wallets) => {
    if (answered.current) return;
    answered.current = true;
    setState(walletUsable(w) ? 'ready' : 'none');
    latest.current.onAvailable(w);
  };
  // If Stripe never answers (it normally does within a second or two), carry on without a wallet row.
  useEffect(() => {
    const t = setTimeout(() => {
      if (answered.current) return;
      console.warn('[tikkas] Express Checkout: no ready event after 8 s, treating wallets as unavailable');
      settle({ applePay: false, googlePay: false });
    }, 8000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onReady = (e: StripeExpressCheckoutElementReadyEvent) => {
    const pm = e.availablePaymentMethods;
    const wallets = { applePay: !!pm?.applePay, googlePay: !!pm?.googlePay };
    console.log('[tikkas] Express Checkout ready', { availablePaymentMethods: pm ?? null, env: walletEnv() });
    settle(wallets);
  };
  const onLoadError = (e: { error: { message?: string; code?: string; type?: string } }) => {
    console.error('[tikkas] Express Checkout loaderror', e.error);
    settle({ applePay: false, googlePay: false });
  };
  /** The native sheet was confirmed: create (or reuse) the order, take the client secret, confirm, then tell the server. */
  const onConfirm = async (e: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) return;
    const own = latest.current;
    setPaying(true);
    own.onBusy?.(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) throw new PayError(submitError.message ?? 'Could not start the payment');
      const p = await own.createPayable();
      if (!p.requiresPayment) return await own.onPaid('');
      const r = await api<PayRes>(`${payBase(p)}/pay${payQuery(p)}`, { body: { method: 'wallet' } });
      if (r.status === 'succeeded') return await own.onPaid(r.payLabel ?? 'card');
      if (r.status !== 'wallet' || !r.clientSecret) throw new PayError(r.message ?? 'Could not start the payment');
      if (p.cardPence !== own.amountPence) elements.update({ amount: p.cardPence });
      const { error } = await stripe.confirmPayment({ elements, clientSecret: r.clientSecret, confirmParams: { return_url: `${WEB_URL}/pay/return?kind=${p.kind}&id=${p.id}${p.returnExtra ?? ''}` }, redirect: 'if_required' });
      if (error) throw new PayError(error.message ?? 'Payment failed');
      const c = await api<PayRes>(`${payBase(p)}/confirm${payQuery(p)}`, { body: {} });
      if (c.status === 'succeeded') return await own.onPaid(c.payLabel ?? 'Apple Pay');
      throw new PayError(c.message ?? 'The payment did not go through.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Payment failed';
      e.paymentFailed({ reason: 'fail' });
      own.onError(msg);
    } finally {
      setPaying(false);
      own.onBusy?.(false);
    }
  };
  const show = props.selected && state !== 'none';
  return (
    <div className={`ece-box${show ? '' : ' ece-off'}`} aria-hidden={!show}>
      {show && state === 'loading' && <div className="ece-skeleton" aria-hidden="true" />}
      <ExpressCheckoutElement
        options={{
          paymentMethods: { link: 'never', applePay: 'always', googlePay: 'auto', paypal: 'never', amazonPay: 'never', klarna: 'never' },
          buttonType: { applePay: 'order', googlePay: 'order' },
          buttonTheme: { applePay: 'black', googlePay: 'black' },
          buttonHeight: 52,
          // No `overflow: 'never'`: with it, Stripe's Express Checkout Element never fires `ready` (reproduced 15 Oct 2026 on
          // Stripe.js v3 with both the platform and connected-account instances), so no wallet ever appears.
          layout: { maxColumns: 1, maxRows: 2 },
        }}
        onReady={onReady}
        onLoadError={onLoadError}
        onClick={(ev) => ev.resolve()}
        onConfirm={onConfirm}
      />
      {paying && <div className="ece-busy"><Spinner /></div>}
    </div>
  );
}

/* ---------- the native sheet (the iPhone and Android apps) ---------- */

/**
 * The same row and the same server calls as the web button, with Stripe's native sheet in place of the Express Checkout
 * Element (docs/32): the plugin says which wallet the phone has, our black Apple Pay / Google Pay button opens the sheet
 * for the intent the server made, and the server is told to confirm afterwards. Stays mounted so its answer is kept.
 */
function NativeWalletButton(props: WalletProps) {
  const latest = useRef(props);
  latest.current = props;
  const [wallets, setWallets] = useState<Wallets | null>(null);
  const [paying, setPaying] = useState(false);
  useEffect(() => {
    let live = true;
    nativeWallets()
      .catch((e: unknown) => {
        console.error('[tikkas] native wallet check failed', e);
        return { applePay: false, googlePay: false };
      })
      .then((w) => {
        if (!live) return;
        console.log('[tikkas] native wallet', w);
        setWallets(w);
        latest.current.onAvailable(w);
      });
    return () => {
      live = false;
    };
  }, []);
  const pay = async () => {
    if (paying) return;
    const own = latest.current;
    setPaying(true);
    own.onBusy?.(true);
    try {
      const p = await own.createPayable();
      if (!p.requiresPayment) return await own.onPaid('');
      const r = await api<PayRes>(`${payBase(p)}/pay${payQuery(p)}`, { body: { method: 'wallet' } });
      if (r.status === 'succeeded') return await own.onPaid(r.payLabel ?? 'card');
      if (r.status !== 'wallet' || !r.clientSecret || !r.stripeAccountId) throw new PayError(r.message ?? 'Could not start the payment');
      // The server names the key and the Merchant ID; an API older than the app leaves them out, and the app's own values serve.
      const outcome = await presentNativeWallet({ publishableKey: r.publishableKey ?? own.pk, stripeAccountId: r.stripeAccountId, clientSecret: r.clientSecret, amountPence: p.cardPence, applePayMerchantId: r.applePayMerchantId ?? APPLE_PAY_MERCHANT_ID, label: 'Tikkas' });
      if (outcome === 'cancelled') return;
      const c = await api<PayRes>(`${payBase(p)}/confirm${payQuery(p)}`, { body: {} });
      if (c.status === 'succeeded') return await own.onPaid(c.payLabel ?? (wallets?.googlePay ? 'Google Pay' : 'Apple Pay'));
      throw new PayError(c.message ?? 'The payment did not go through.');
    } catch (err) {
      own.onError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setPaying(false);
      own.onBusy?.(false);
    }
  };
  if (!props.selected || props.amountPence <= 0 || (wallets && !walletUsable(wallets))) return null;
  const apple = !wallets || wallets.applePay;
  return (
    <div className="ece-box">
      {!wallets ? <div className="ece-skeleton" aria-hidden="true" /> : (
        <button type="button" className={`native-pay ${apple ? 'apple' : 'google'}`} disabled={paying} onClick={pay} aria-label={apple ? 'Pay with Apple Pay' : 'Pay with Google Pay'}>
          <Svg html={apple ? APPLE_LOGO : GOOGLE_G} />Pay
        </button>
      )}
      {paying && <div className="ece-busy"><Spinner /></div>}
    </div>
  );
}
